import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/UserContext';
import { useOrgAccess } from '../../shared/contexts';
import { buildFunctionUrl } from '../../shared/utils';

// Every org call carries its own deadline. A request that never answers used to
// strand the console: `loading` stayed true (permanent skeleton, and the refresh
// control disables itself on `loading`), or `busy` stayed true and every action
// went dead with no way back. Reads are quick lookups; mutations may do Stripe
// work server-side first, so they get the longer deadline.
export const ORG_READ_TIMEOUT_MS = 20000;
export const ORG_MUTATION_TIMEOUT_MS = 30000;
export const REQUEST_TIMEOUT_MESSAGE = 'Request timed out. Check your connection and retry.';

// Fetch honors the abort signal on its own; minting the ID token does not, so
// it is raced against the same deadline. The listener is dropped as soon as the
// wrapped promise settles, so nothing is left dangling either way.
function withDeadline(promise, signal) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error(REQUEST_TIMEOUT_MESSAGE));
      return;
    }
    const onAbort = () => reject(new Error(REQUEST_TIMEOUT_MESSAGE));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}

// All org admin/billing data flows through the role-gated Cloud Functions
// (getOrg, listOrgMembers, getOrgCreditPool, getOrgBilling, ...). The client
// NEVER reads the organizations/{orgId} doc directly — that holds the pooled
// credit balance, which is METADATA-gated to Org / Billing Admins server-side.
async function callOrgFn(currentUser, name, body = {}, { timeoutMs = ORG_READ_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  let timedOut = false;
  const deadline = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  try {
    const idToken = await withDeadline(currentUser.getIdToken(), controller.signal);
    const res = await fetch(buildFunctionUrl(name), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // `status` marks a real answer from the server, so the timeout branch
      // below can never relabel a genuine failure as a timeout.
      const failure = new Error(data?.error || `${name} failed (${res.status})`);
      failure.status = res.status;
      throw failure;
    }
    return data;
  } catch (callError) {
    if (timedOut && callError?.status === undefined) {
      throw new Error(REQUEST_TIMEOUT_MESSAGE);
    }
    throw callError;
  } finally {
    clearTimeout(deadline);
  }
}

/**
 * Drives the org Admin panel. Loads the org summary, roster + per-member usage,
 * the credit pool, and billing, and exposes the seat / role / invite / top-up
 * actions. Role-gated: a non-admin gets `canAccessAdmin === false` and the
 * screen renders a "no access" state.
 */
export function useAdminScreen() {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const access = useOrgAccess();
  const { canAccessAdmin, isBillingAdmin, isOrgAdmin, orgId, isLoading: accessLoading } = access;

  const [tab, setTab] = useState('seats');
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [creditPool, setCreditPool] = useState(null);
  const [billing, setBilling] = useState(null);
  const [billingError, setBillingError] = useState(null);
  const [loading, setLoading] = useState(true);
  // True only after the complete role-gated organization payload has loaded at
  // least once. Keep this separate from `loading`: if the initial request
  // fails, null arrays and counters must not be presented as real zero values.
  const [coreDataLoaded, setCoreDataLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [actionSeverity, setActionSeverity] = useState('success');
  const [busy, setBusy] = useState(false);
  // Which mutation is in flight (`pendingAction`) and, when it targets a single
  // row, which row (`pendingKey`: a member id, an invited email). `busy` keeps
  // its old meaning for the page-level progress bar, but these two let a single
  // control show its own pending state instead of the whole console going dead
  // on one role change.
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingKey, setPendingKey] = useState(null);
  // Genuine client timestamp of when the role-gated data last resolved; powers
  // the quiet "Updated …" freshness label (the screenshots' data-provenance cue).
  const [loadedAt, setLoadedAt] = useState(null);

  // Audit log (enterprise activity trail) loads lazily, separate from the main
  // refresh: the org Activity view fetches a page on demand and appends more via
  // the cursor. Reads through the role-gated getOrgAuditLog Cloud Function; the
  // backend denies all client access to the auditLog collection directly.
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditCursor, setAuditCursor] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [auditLoadedAt, setAuditLoadedAt] = useState(null);

  const refresh = useCallback(async () => {
    if (!currentUser || !canAccessAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setBillingError(null);
    try {
      const [orgRes, membersRes, poolRes, billingResult] = await Promise.all([
        callOrgFn(currentUser, 'getOrg'),
        callOrgFn(currentUser, 'listOrgMembers'),
        callOrgFn(currentUser, 'getOrgCreditPool'),
        callOrgFn(currentUser, 'getOrgBilling')
          .then((data) => ({ data, error: null }))
          .catch((billingFetchError) => ({ data: null, error: billingFetchError })),
      ]);
      if (!orgRes?.org || !poolRes) {
        throw new Error('Organization data is temporarily unavailable');
      }
      setOrg(orgRes.org);
      setMembers(Array.isArray(membersRes?.members) ? membersRes.members : []);
      setPendingInvites(Array.isArray(membersRes?.pendingInvites) ? membersRes.pendingInvites : []);
      setCreditPool(poolRes || null);
      setCoreDataLoaded(true);
      if (billingResult.error) {
        setBillingError(billingResult.error.message || 'Billing data is temporarily unavailable');
      } else {
        setBilling(billingResult.data || null);
      }
      setLoadedAt(Date.now());
    } catch (e) {
      setError(e.message || 'Failed to load organization data');
    } finally {
      setLoading(false);
    }
  }, [currentUser, canAccessAdmin]);

  useEffect(() => {
    if (!accessLoading) {
      refresh();
    }
  }, [accessLoading, refresh]);

  // Stripe Checkout returns to the console with a short status marker. Consume
  // it once, remove it from the URL, and refresh after a completed top-up.
  useEffect(() => {
    if (!canAccessAdmin || typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const topUpStatus = url.searchParams.get('topup');
    if (!topUpStatus) return;
    url.searchParams.delete('topup');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    if (topUpStatus === 'success') {
      setActionSeverity('success');
      setActionMsg('Credit top-up completed. The refreshed balance will appear shortly.');
      refresh();
    } else if (topUpStatus === 'cancel') {
      setActionSeverity('info');
      setActionMsg('Credit top-up was cancelled. No charge was made.');
    }
  }, [canAccessAdmin, refresh]);

  // Lazy-load a page of audit events. reset:true starts a fresh page (cursor
  // cleared); otherwise it appends the next page using the stored cursor.
  const loadAuditLog = useCallback(async ({ reset = false } = {}) => {
    if (!currentUser || !canAccessAdmin) return;
    setAuditLoading(true); setAuditError(null);
    try {
      const data = await callOrgFn(currentUser, 'getOrgAuditLog', { limit: 50, cursorIso: reset ? null : auditCursor });
      const incoming = Array.isArray(data?.events) ? data.events : [];
      setAuditEvents((prev) => (reset ? incoming : [...prev, ...incoming]));
      setAuditCursor(data?.nextCursor || null);
      setAuditLoaded(true);
      setAuditLoadedAt(Date.now());
    } catch (e) { setAuditError(e.message || 'Failed to load activity'); }
    finally { setAuditLoading(false); }
  }, [currentUser, canAccessAdmin, auditCursor]);

  // Mutations run through here. `action` (and `key` for a row-scoped mutation)
  // records WHAT is in flight so the UI can scope its pending state to the one
  // control that is acting. Everything is cleared in `finally`, including after
  // a timeout, so the console always recovers.
  const runAction = useCallback(async (fn, successMsg, { action = null, key = null } = {}) => {
    setBusy(true);
    setPendingAction(action);
    setPendingKey(key);
    setActionMsg(null);
    setActionSeverity('success');
    setError(null);
    try {
      await fn();
      if (successMsg) setActionMsg(successMsg);
      await refresh();
      return true;
    } catch (e) {
      setError(e.message || 'Action failed');
      return false;
    } finally {
      setBusy(false);
      setPendingAction(null);
      setPendingKey(null);
    }
  }, [refresh]);

  // Mutations get the longer deadline: adjusting seats or starting a checkout
  // makes the backend talk to Stripe before it answers.
  const callOrgMutation = useCallback(
    (name, body) => callOrgFn(currentUser, name, body, { timeoutMs: ORG_MUTATION_TIMEOUT_MS }),
    [currentUser]
  );

  const inviteMember = useCallback((email) =>
    runAction(() => callOrgMutation('inviteOrgMember', { email }), `Invited ${email}`,
      { action: 'inviteMember', key: email }),
    [callOrgMutation, runAction]);

  // Revoke a pending invite, freeing its reserved seat.
  const cancelInvite = useCallback((email) =>
    runAction(() => callOrgMutation('cancelOrgInvite', { email }), `Cancelled invite to ${email}`,
      { action: 'cancelInvite', key: email }),
    [callOrgMutation, runAction]);

  const deactivateMember = useCallback((targetUserId, label) =>
    runAction(() => callOrgMutation('deactivateOrgMember', { targetUserId }), `Deactivated ${label || 'member'}, seat freed`,
      { action: 'deactivateMember', key: targetUserId }),
    [callOrgMutation, runAction]);

  const setMemberRoles = useCallback((targetUserId, roles) =>
    runAction(() => callOrgMutation('setOrgMemberRoles', { targetUserId, roles }), 'Roles updated',
      { action: 'setMemberRoles', key: targetUserId }),
    [callOrgMutation, runAction]);

  const setBillingContact = useCallback((targetUserId, label) =>
    runAction(
      () => callOrgMutation('setOrgBillingContact', { targetUserId }),
      `${label || 'Member'} is now the billing contact`,
      { action: 'setBillingContact', key: targetUserId }
    ),
    [callOrgMutation, runAction]);

  // Domain joining is paused until ownership verification exists. The UI sends
  // the full retained list only when an admin removes a legacy domain claim.
  const setDomains = useCallback((domains) =>
    runAction(() => callOrgMutation('setOrgDomains', { domains }), 'Domain claim removed',
      { action: 'setDomains' }),
    [callOrgMutation, runAction]);

  // Org Admin: opt the firm in/out of the cross-app connector (the conductor
  // bridge across PowerPoint, Excel, and Word). On by default; this writes
  // entitlements.conductor = enabled on the org.
  const setConductor = useCallback((enabled) =>
    runAction(() => callOrgMutation('setOrgConductor', { enabled }),
      enabled ? 'Cross-app connector turned on' : 'Cross-app connector turned off',
      { action: 'setConductor' }),
    [callOrgMutation, runAction]);

  // Org Admin: route ordinary Claude turns through the shared opencode loop.
  // On by default; false selects the retained Claude Agent SDK loop.
  const setClaudeViaOpencode = useCallback((enabled) =>
    runAction(() => callOrgMutation('setOrgClaudeViaOpencode', { enabled }),
      enabled ? 'Claude now uses the opencode loop' : 'Claude now uses the Agent SDK loop',
      { action: 'setClaudeViaOpencode' }),
    [callOrgMutation, runAction]);

  const adjustSeats = useCallback(({
    targetSeats,
    expectedCurrentSeats,
    expectedOperationId,
    expectedQuoteAmountMinor,
    expectedQuoteCurrency,
    expectedQuoteProrationDate,
  }) =>
    runAction(
      () => callOrgMutation('adjustOrgSeats', {
        targetSeats,
        expectedCurrentSeats,
        expectedOperationId,
        expectedQuoteAmountMinor,
        expectedQuoteCurrency,
        expectedQuoteProrationDate,
      }),
      `Seat count updated to ${targetSeats}`,
      { action: 'adjustSeats' }
    ),
    [callOrgMutation, runAction]);

  const previewSeatChange = useCallback((targetSeats) =>
    callOrgMutation('previewOrgSeatChange', { targetSeats }), [callOrgMutation]);

  // Payment method, invoices, and billing details are Stripe's surface — the
  // customer portal. We only hand over a return URL back to this section.
  const openPortal = useCallback(async () => {
    setBusy(true);
    setPendingAction('openPortal');
    setPendingKey(null);
    setError(null);
    try {
      const data = await callOrgMutation('createPortalSession', {
        returnUrl: `${window.location.origin}/settings/organization/billing`,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError('Could not open the billing portal. Please try again.');
      }
    } catch (e) {
      setError(e.message || 'Could not open the billing portal');
    } finally {
      setBusy(false);
      setPendingAction(null);
    }
  }, [callOrgMutation]);

  // Top-up opens Stripe Checkout. Booster keys mirror the backend catalog.
  const topUp = useCallback(async (packKey) => {
    if (billingError || !billing) {
      setError('Billing details must be verified before starting a top-up. Refresh and try again.');
      return false;
    }
    setBusy(true);
    setPendingAction('topUp');
    setPendingKey(packKey);
    setError(null);
    try {
      const data = await callOrgMutation('createOrgTopUp', {
        packKey,
        successUrl: `${window.location.origin}/settings/credits?topup=success`,
        cancelUrl: `${window.location.origin}/settings/credits?topup=cancel`,
      });
      const url = data?.url || data?.sessionUrl || data?.checkoutUrl || data?.session?.url;
      if (url) {
        window.location.href = url;
        return true;
      } else {
        setError('Could not start checkout. Please try again.');
        return false;
      }
    } catch (e) {
      setError(e.message || 'Top-up failed');
      return false;
    } finally {
      setBusy(false);
      setPendingAction(null);
      setPendingKey(null);
    }
  }, [callOrgMutation, billing, billingError]);

  return {
    navigate,
    isAuthenticated,
    accessLoading,
    canAccessAdmin,
    isBillingAdmin,
    isOrgAdmin,
    canManageBilling: isOrgAdmin || isBillingAdmin,
    orgId,
    tab,
    setTab,
    org,
    members,
    pendingInvites,
    creditPool,
    billing,
    billingError,
    loading,
    coreDataLoaded,
    error,
    actionMsg,
    actionSeverity,
    busy,
    pendingAction,
    pendingKey,
    loadedAt,
    auditEvents,
    auditCursor,
    auditLoading,
    auditLoaded,
    auditError,
    auditLoadedAt,
    loadAuditLog,
    setError,
    refresh,
    inviteMember,
    cancelInvite,
    deactivateMember,
    setMemberRoles,
    setBillingContact,
    setDomains,
    setConductor,
    setClaudeViaOpencode,
    adjustSeats,
    previewSeatChange,
    openPortal,
    topUp,
  };
}
